import { types, flow, getRoot } from 'mobx-state-tree';
import pdfCourtFormsService from '~/src/services/pdfCourtFormsService';

import createPaginatedModel from '~/src/stores/composers/createPaginatedModel';

import {
  getTemplateIdentifier,
  mapPdfCourtForm,
} from '~/src/utils/dataTransformers';
import { history } from '../utils/history';

const PdfCourtFormStore = types.model('PdfCourtFormStore', {
  identifier: types.identifier,
  id: types.string,
  filePath: types.maybeNull(types.string),
  fprint: types.maybeNull(types.string),
  filterCategory: types.maybeNull(types.string),
  locality: types.maybeNull(types.string),
  title: types.maybeNull(types.string),
  subtitle: types.maybeNull(types.string),
  documentCategoryId: types.maybeNull(types.number),
  legalDocumentType: types.maybeNull(types.string),
  legalPracticeArea: types.maybeNull(types.string),
  isDeleted: types.boolean,
});

const PdfCourtFormsStore = types
  .model('PdfCourtFormsStore', {
    isLoading: false,
    error: '',
  })
  .actions((self) => {
    const fetch = flow(function* (query, requestPayload) {
      return yield self.paginate(query || {}, requestPayload);
    });

    const addTemplateToSeletionSidebar = (template) => {
      const store = getRoot(self);
      store.sidebarItems.add({
        ...template,
      });
    };

    // Master search results come straight from the search API and never pass
    // through `paginate`, so they're absent from `dictionary` and carry the raw
    // snake_case shape (`document_type`) rather than the mapped `documentCategoryId`.
    // Map the raw result and seed the dictionary so it resolves the same way a
    // folder-navigated form does, then return the stored (mapped) template.
    const seedSearchResult = (searchResult) => {
      const template = mapPdfCourtForm({ document: searchResult });
      if (!self.dictionary.has(template.identifier)) {
        self.addItem(template.identifier, template);
      }
      return self.dictionary.get(template.identifier);
    };

    // The draft screen (TemplateSetDraft) resolves selected ids against
    // `selectOptions()` (i.e. the dictionary), so a search-only form would be
    // dropped before reaching the draft endpoint without seeding first.
    const addSearchResultToSelectionSidebar = (searchResult) => {
      addTemplateToSeletionSidebar(seedSearchResult(searchResult));
    };

    const createFormTemplate = flow(function* (template) {
      const store = getRoot(self);
      const { id } =
        yield store.templateDefaultValues.createTemplateDefaultValues(
          template.id,
        );
      history.push(`/form-template/${id}`);
    });

    return {
      fetch,
      addTemplateToSeletionSidebar,
      addSearchResultToSelectionSidebar,
      seedSearchResult,
      createFormTemplate,
    };
  })
  .views((self) => {
    function getTemplate(templateId) {
      return self.dictionary.get(getTemplateIdentifier(templateId));
    }

    function getTemplatePreview(templateId) {
      return pdfCourtFormsService.getTemplatePreview(templateId);
    }

    function selectOptions() {
      return Array.from(self.dictionary.values()).map((template) => ({
        value: template.id,
        label: template.title,
        subtitle: template.subtitle,
        documentType: template.documentCategoryId,
      }));
    }

    return {
      getTemplate,
      getTemplatePreview,
      selectOptions,
    };
  });

const PaginatedPdfCourtFormsStore = createPaginatedModel(
  'PaginatedPdfCourtFormsStore',
  PdfCourtFormsStore,
  PdfCourtFormStore,
  { paginate: pdfCourtFormsService.getPdfCourtForms },
);

export { PaginatedPdfCourtFormsStore as default, PdfCourtFormStore };
